/**
 * Which survey the whole page is about.
 *
 * Everything below it — the funnel, the emails, the schedule, all three tabs — reads
 * whatever this selects. That is the point: a campaign is only useful if the numbers
 * you are looking at belong to it, and a switcher that changed some panels but not
 * others would be worse than none.
 *
 * Creating is CLONING by default. A survey that already works carries nine validated
 * questions, four written emails and a matching csv_columns list; starting blank
 * means reproducing all of that before anything can be sent.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../../services/api';
import { usePermissionAccess } from '../auth/RoleGuard';
import { SurveyCampaign } from './types';

const TEAL = '#1A5F7A';

interface Props {
  value: string | null;
  /**
   * The full campaign is handed back, not just the id, so the page can title itself
   * with the survey you are actually looking at. Without it the heading kept saying
   * "Customer Discovery Survey" while a different campaign's numbers were on screen —
   * the one part of the page the switcher did not move, and the most confident-looking.
   */
  onChange: (id: string, campaign?: SurveyCampaign) => void;
}

/** Mirrors the Lambda's CAMPAIGN_ID_RE — this becomes a DynamoDB key and a URL. */
const ID_RE = /^[a-z0-9][a-z0-9-]{2,63}$/;

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

const CampaignSwitcher: React.FC<Props> = ({ value, onChange }) => {
  const { hasFullAccess } = usePermissionAccess();
  const canEdit = hasFullAccess('audience_manager');

  const [campaigns, setCampaigns] = useState<SurveyCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  /** Returns the list it fetched, so a caller can act on it without waiting for state. */
  const load = async (): Promise<SurveyCampaign[]> => {
    setLoading(true);
    let list: SurveyCampaign[] = [];
    try {
      const res = await apiService.getSurveyCampaigns();
      list = res?.campaigns || [];
      setCampaigns(list);
      // Land on the live campaign rather than whatever sorts first.
      if (!value && list.length) {
        const initial = list.find((c) => c.is_default) || list[0];
        onChange(initial.id, initial);
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load the campaigns.');
    }
    setLoading(false);
    return list;
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await apiService.createSurveyCampaign({
        id,
        title: title || undefined,
        // Clone whatever is on screen, so "make one like this" is the default act.
        from: value || undefined,
        promo_code: promoCode || undefined,
      });
      setOpen(false);
      setTitle('');
      setId('');
      setIdTouched(false);
      setPromoCode('');
      const list = await load();
      onChange(res.id, (list || []).find((c: SurveyCampaign) => c.id === res.id));
    } catch (e: any) {
      const msg = String(e?.message || '');
      setError(
        /409/.test(msg)
          ? 'That name or tag prefix is already taken by another campaign.'
          : msg || 'Could not create the campaign.'
      );
    }
    setCreating(false);
  };

  const idError = id !== '' && !ID_RE.test(id);
  const current = campaigns.find((c) => c.id === value);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Survey"
          value={campaigns.length && value ? value : ''}
          onChange={(e) =>
            onChange(e.target.value, campaigns.find((c) => c.id === e.target.value))
          }
          disabled={loading || !campaigns.length}
          sx={{ minWidth: 260 }}
        >
          {campaigns.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.title || c.id}
              {c.status && c.status !== 'active' ? ` — ${c.status}` : ''}
            </MenuItem>
          ))}
        </TextField>

        {current && !current.is_default && (
          <Chip
            size="small"
            label={current.status === 'draft' ? 'Draft — nothing sends yet' : current.status}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }}
          />
        )}

        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => {
            setId('');
            setTitle('');
            setIdTouched(false);
            setPromoCode('');
            setError(null);
            setOpen(true);
          }}
          disabled={!canEdit}
          sx={{ textTransform: 'none', color: TEAL, fontWeight: 600 }}
        >
          New survey
        </Button>
      </Box>

      <Dialog open={open} onClose={() => !creating && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 16, fontWeight: 700 }}>
          New survey
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: 13, bgcolor: '#F8FAFC' }}>
            This copies <strong>{current?.title || value}</strong> — its questions, all four
            emails and its CSV columns. It starts as a draft with its own tracking tag, so
            nothing is sent until you invite people to it.
          </Alert>

          <TextField
            fullWidth
            size="small"
            label="Name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              // Derive the id until someone edits it themselves, then leave it alone.
              if (!idTouched) setId(slugify(e.target.value));
            }}
            sx={{ mb: 2 }}
            placeholder="Churn exit survey"
          />

          <TextField
            fullWidth
            size="small"
            label="Identifier"
            value={id}
            onChange={(e) => {
              setIdTouched(true);
              setId(e.target.value.toLowerCase());
            }}
            error={idError}
            helperText={
              idError
                ? '3–64 characters: lowercase letters, numbers and hyphens.'
                : 'Used in the survey link and in the tracking tags. It cannot be changed later.'
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Discount code (optional)"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            helperText={
              'Left blank, this survey has no offer until you add one. It deliberately does NOT ' +
              'inherit the other survey’s code — that would share one redemption cap between two ' +
              'surveys. The code must also exist in Stripe and App Store Connect before it works.'
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={creating} sx={{ textTransform: 'none', color: '#6B7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={create}
            disabled={creating || !ID_RE.test(id)}
            sx={{ textTransform: 'none', bgcolor: TEAL, '&:hover': { bgcolor: '#14506B' } }}
          >
            {creating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignSwitcher;
